import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { DownOutlined, RightOutlined } from '@ant-design/icons';

import NavItem from './NavItem';
import { handlerDrawerOpen, isDrawerVisualOpen, useGetMenuMaster } from 'api/menu';

function childMatchesPath(children, pathname) {
  if (!children) return false;
  for (const child of children) {
    if (child.type === 'item' && child.url && (pathname === child.url || pathname.startsWith(`${child.url}/`))) {
      return true;
    }
    if (child.type === 'collapse' && childMatchesPath(child.children, pathname)) {
      return true;
    }
  }
  return false;
}

export default function NavCollapse({ menu, level }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = isDrawerVisualOpen(menuMaster);
  const pinned = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => childMatchesPath(menu.children, pathname));

  useEffect(() => {
    if (childMatchesPath(menu.children, pathname)) {
      setOpen(true);
    }
  }, [pathname, menu.children]);

  const handleClick = () => {
    if (!pinned) {
      handlerDrawerOpen(true);
      setOpen(true);
      return;
    }
    setOpen((prev) => !prev);
  };

  const Icon = menu.icon;
  const menuIcon = Icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : null;

  const navChildren = menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return null;
    }
  });

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' }
          })
        }}
      >
        {menuIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'secondary.lighter' }
              })
            }}
          >
            {menuIcon}
          </ListItemIcon>
        )}
        {drawerOpen && (
          <>
            <ListItemText
              primary={
                <Typography variant="h6" color="text.primary">
                  {t(menu.title, menu.title)}
                </Typography>
              }
            />
            {open ? <DownOutlined style={{ fontSize: '0.75rem' }} /> : <RightOutlined style={{ fontSize: '0.75rem' }} />}
          </>
        )}
      </ListItemButton>
      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box>{navChildren}</Box>
        </Collapse>
      )}
    </>
  );
}

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number
};
